// hooks/useOptions.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../services/axiosInterceptor';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * The top-level API envelope from GET /api/user-options/multiple.
 * `data` maps option_name -> raw unwrapped option_value (see
 * GetMultipleUserOptions in the Go controller — each entry is the
 * decoded value itself, not a per-option {status, data} wrapper).
 */
interface MultipleOptionsApiResponse<T = unknown> {
  status: 'success' | 'error';
  data: Record<string, T>;
  message?: string;
}

/** State shape for a single named option */
export interface OptionState<T = unknown> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/** The value returned by the hook */
export interface UseOptionsReturn<T = unknown> {
  /** One entry per option name you requested */
  options: Record<string, OptionState<T>>;
  /** True while ANY option is still loading */
  loading: boolean;
  /** First error encountered across all options (null if none) */
  error: string | null;
  /** Manually re-fetch all options */
  refetch: () => void;
}

// ─── Config ───────────────────────────────────────────────────────────────────

export interface UseOptionsConfig {
  /**
   * Whether to fetch immediately on mount.
   * Set to false if you want to call `refetch()` manually.
   * @default true
   */
  enabled?: boolean;
  /**
   * Re-fetch whenever this value changes (like a dependency in useEffect).
   * Useful when your option names depend on other state.
   */
  deps?: unknown[];
  /**
   * Pass through to GET /api/user-options/multiple?school_id=... — required
   * when the caller is a superadmin (their JWT has no implicit school).
   */
  schoolId?: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useOptions — fetch one or many school-scoped user options from
 * GET /api/user-options/multiple.
 *
 * @example Single option
 * ```ts
 * const { options } = useOptions<string[]>('school_zone');
 * const zone = options['school_zone'].data; // string[] | null
 * ```
 *
 * @example Multiple options
 * ```ts
 * const { options, loading } = useOptions<unknown>(['school_zone', 'current_session']);
 * const zone    = options['school_zone'].data;
 * const session = options['current_session'].data;
 * ```
 *
 * @example Superadmin (must pass schoolId explicitly)
 * ```ts
 * const { options } = useOptions(['school_zone'], { schoolId });
 * ```
 */
export function useOptions<T = unknown>(
  names: string | string[],
  config: UseOptionsConfig = {}
): UseOptionsReturn<T> {
  const { enabled = true, deps = [], schoolId } = config;

  // Normalise to a stable comma-joined key so we can use it as an effect dep
  const namesArray = Array.isArray(names) ? names : [names];
  const namesKey = namesArray.join(',');

  // Build the initial loading state (one entry per name)
  const buildInitialState = useCallback(
    (): Record<string, OptionState<T>> =>
      namesArray.reduce(
        (acc, name) => ({
          ...acc,
          [name]: { data: null, loading: true, error: null },
        }),
        {} as Record<string, OptionState<T>>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [namesKey]
  );

  const [options, setOptions] = useState<Record<string, OptionState<T>>>(
    buildInitialState
  );

  // A counter we increment to trigger a re-fetch from outside
  const [fetchIndex, setFetchIndex] = useState(0);
  const refetch = useCallback(() => setFetchIndex((n) => n + 1), []);

  // Track whether the component is still mounted so we don't set state after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Reset to loading whenever names / fetch trigger changes
    setOptions(buildInitialState());

    let cancelled = false;

    const fetchOptions = async () => {
      try {
        const response = await apiClient.get<MultipleOptionsApiResponse<T>>(
          'api/user-options/multiple',
          { params: { names: namesKey, ...(schoolId ? { school_id: schoolId } : {}) } }
        );
        console.log('tt',response.data)

        if (cancelled || !mountedRef.current) return;

        if (response.data.status === 'success') {
          const incoming = response.data.data ?? {};

          // Each entry in `incoming` is the raw decoded value for that
          // option name — the backend does not wrap per-option status.
          // Absence of the key (not returned at all) is the only failure
          // signal available per-name.
          setOptions(
            namesArray.reduce((acc, name) => {
              const present = Object.prototype.hasOwnProperty.call(incoming, name);

              acc[name] = present
                ? { data: (incoming[name] as T) ?? null, loading: false, error: null }
                : {
                    data: null,
                    loading: false,
                    error: `Option "${name}" was not returned by the server.`,
                  };

              return acc;
            }, {} as Record<string, OptionState<T>>)
          );
        } else {
          // Top-level API error — mark all options as failed
          const apiError = response.data.message ?? 'Failed to load options.';
          setOptions(
            namesArray.reduce(
              (acc, name) => ({
                ...acc,
                [name]: { data: null, loading: false, error: apiError },
              }),
              {} as Record<string, OptionState<T>>
            )
          );
        }
      } catch (err: unknown) {
        if (cancelled || !mountedRef.current) return;

        const message =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ??
          (err as { message?: string })?.message ??
          'An unexpected error occurred while fetching options.';

        setOptions(
          namesArray.reduce(
            (acc, name) => ({
              ...acc,
              [name]: { data: null, loading: false, error: message },
            }),
            {} as Record<string, OptionState<T>>
          )
        );
      }
    };

    fetchOptions();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namesKey, enabled, fetchIndex, schoolId, buildInitialState, ...deps]);

  // Derive aggregate loading / error
  const values = Object.values(options);
  const loading = values.some((o) => o.loading);
  const error = values.find((o) => o.error)?.error ?? null;

  return { options, loading, error, refetch };
}